import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Avatar,
  Box,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Chip,
  Stack,
  IconButton,
  Tooltip,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CampaignIcon from "@mui/icons-material/Campaign";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import FolderIcon from "@mui/icons-material/Folder";
import CloseIcon from "@mui/icons-material/Close";
import moment from "moment";
import "moment/locale/es";
import ENV from "../../config/env";
import { splitTituloContenido, esVideoNoticia, esImagenNoticia, extraerYoutubeId, textoParaMostrar, extraerPlayStore } from "../../utils/noticiasHelpers";
import YoutubeEmbed from "../news/YoutubeEmbed";
import PlayStoreCard from "../news/PlayStoreCard";

const firstChar = (s) => (s ? String(s).trim().charAt(0).toUpperCase() : "?");

// Portada del anuncio: video de YouTube, video subido, imagen (completa, sobre un fondo desenfocado de sí misma
// para no dejar franjas vacías) o, si no tiene nada de eso, una franja ilustrada con el megáfono.
function Portada({ noticia, youtubeId, titulo }) {
  const url = noticia.archivo_url ? `${ENV.API_BASE_URL}${noticia.archivo_url}` : "";

  if (youtubeId) return <YoutubeEmbed videoId={youtubeId} sx={{ borderRadius: 0 }} />;

  if (esVideoNoticia(noticia.archivo_url)) {
    return (
      <Box
        component="video"
        src={url}
        controls
        preload="metadata"
        playsInline
        sx={{ display: "block", width: "100%", maxHeight: "42vh", bgcolor: "common.black" }}
      />
    );
  }

  if (esImagenNoticia(noticia.archivo_url)) {
    return (
      <Tooltip title="Ver imagen en tamaño original" placement="bottom">
        <Box
          component="a"
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ position: "relative", display: "block", height: "clamp(200px, 40vh, 360px)", overflow: "hidden", bgcolor: "common.black", cursor: "zoom-in" }}
        >
          <Box
            aria-hidden
            sx={{
              position: "absolute",
              inset: -24,
              backgroundImage: `url("${url}")`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              filter: "blur(22px) brightness(0.7)",
            }}
          />
          <Box
            component="img"
            src={url}
            alt={titulo || "Imagen del anuncio"}
            sx={{ position: "relative", display: "block", width: "100%", height: "100%", objectFit: "contain" }}
          />
        </Box>
      </Tooltip>
    );
  }

  return (
    <Box
      sx={(theme) => ({
        height: 132,
        display: "grid",
        placeItems: "center",
        backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.18 : 0.08),
        backgroundImage: `radial-gradient(${alpha(theme.palette.primary.main, 0.22)} 1px, transparent 1px)`,
        backgroundSize: "14px 14px",
      })}
    >
      <Box
        sx={(theme) => ({
          width: 68,
          height: 68,
          borderRadius: "50%",
          display: "grid",
          placeItems: "center",
          bgcolor: "background.paper",
          color: "primary.main",
          boxShadow: `0 0 0 8px ${alpha(theme.palette.primary.main, 0.12)}`,
        })}
      >
        <CampaignIcon sx={{ fontSize: 34 }} />
      </Box>
    </Box>
  );
}

export default function NuevoAnuncioDialog({ noticia, open, onClose }) {
  if (!noticia) return null;
  const { titulo, cuerpo } = splitTituloContenido(noticia);
  const youtubeId = extraerYoutubeId(cuerpo);
  const texto = textoParaMostrar(cuerpo);
  const playStore = extraerPlayStore(cuerpo);
  const fecha = noticia.fecha ? moment(noticia.fecha).locale("es") : null;
  const adjuntoDescargable =
    noticia.archivo_url && !esVideoNoticia(noticia.archivo_url) && !esImagenNoticia(noticia.archivo_url);
  const avatar = noticia.profile_picture
    ? (noticia.profile_picture.startsWith("http") ? noticia.profile_picture : `${ENV.API_BASE_URL}${noticia.profile_picture}`)
    : undefined;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="nuevo-anuncio-titulo"
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
    >
      {/* Portada con la etiqueta y el botón de cerrar encima */}
      <Box sx={{ position: "relative", flexShrink: 0 }}>
        <Portada noticia={noticia} youtubeId={youtubeId} titulo={titulo} />
        <Chip
          icon={<CampaignIcon />}
          label="Nuevo anuncio"
          size="small"
          color="primary"
          sx={{ position: "absolute", top: 12, left: 12, fontWeight: 700, boxShadow: 2, pointerEvents: "none" }}
        />
        <IconButton
          onClick={onClose}
          aria-label="Cerrar"
          size="small"
          sx={(theme) => ({
            position: "absolute",
            top: 10,
            right: 10,
            bgcolor: alpha(theme.palette.background.paper, 0.85),
            boxShadow: 1,
            "&:hover": { bgcolor: "background.paper" },
          })}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ px: { xs: 2.5, sm: 3 }, pt: 2.5, pb: 1 }}>
        <Typography
          id="nuevo-anuncio-titulo"
          variant="h5"
          sx={{ fontWeight: 800, lineHeight: 1.25, textWrap: "balance", mb: 1.5 }}
        >
          {titulo || "Sin título"}
        </Typography>

        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 2 }}>
          <Avatar src={avatar} sx={{ width: 32, height: 32, fontSize: "0.85rem", bgcolor: "action.selected", color: "text.secondary" }}>
            {firstChar(noticia.usuario)}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>
              {noticia.usuario || "Usuario"}
            </Typography>
            <Tooltip title={fecha ? fecha.format("DD/MM/YYYY HH:mm") : ""}>
              <Typography variant="caption" color="text.secondary">
                {fecha ? fecha.fromNow() : ""}
              </Typography>
            </Tooltip>
          </Box>
          <Chip size="small" variant="outlined" label={noticia.sucursal || "—"} />
        </Stack>

        {texto && (
          <Typography variant="body1" color="text.secondary" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.65 }}>
            {texto}
          </Typography>
        )}

        {playStore && <PlayStoreCard {...playStore} sx={{ mt: 2 }} />}

        {(adjuntoDescargable || noticia.documento_carpeta_id) && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
            {adjuntoDescargable && (
              <Chip
                icon={<AttachFileIcon />}
                label="Descargar adjunto"
                variant="outlined"
                component="a"
                href={`${ENV.API_BASE_URL}${noticia.archivo_url}`}
                target="_blank"
                rel="noopener noreferrer"
                clickable
              />
            )}
            {noticia.documento_carpeta_id && (
              <Chip
                icon={<FolderIcon />}
                label={`Documentos: ${noticia.documento_carpeta_nombre || "carpeta"}`}
                variant="outlined"
                color="primary"
                component={RouterLink}
                to={`/documentos/${noticia.documento_carpeta_id}`}
                onClick={onClose}
                clickable
              />
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2.5, sm: 3 }, py: 2, gap: 1 }}>
        <Button component={RouterLink} to={`/noticias/${noticia.id}`} onClick={onClose} sx={{ textTransform: "none", fontWeight: 600 }}>
          Ver noticia completa
        </Button>
        <Button onClick={onClose} variant="contained" disableElevation sx={{ px: 3 }}>
          Entendido
        </Button>
      </DialogActions>
    </Dialog>
  );
}
